// views/Profile/components/TransactionFilters.jsx
import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Filter, X, Check } from 'lucide-react';
import DateRangePicker from './DateRangePicker';
import './TransactionFilters.css';

const RESOURCE_TYPE_OPTIONS = [
  { value: 'WORKFLOW_EXECUTION', label: 'Workflow runs' },
  { value: 'ASSISTANT_REQUEST', label: 'Agent requests' },
  { value: 'ASSISTANT_CALL', label: 'Agent calls' },
  { value: 'DATABASE_CALL', label: 'Database calls' },
  { value: 'DATABASE_QUERY', label: 'Database queries' },
  { value: 'TABLE_OPERATION', label: 'Table operations' },
  { value: 'DATA_TRANSFER_GB', label: 'Data transfer' },
];

const TXN_TYPE_OPTIONS = [
  { value: null, label: 'All' },
  { value: 'DEDUCTION', label: 'Use' },
  { value: 'BONUS', label: 'Bonus' },
  { value: 'RECHARGE', label: 'Recharge' },
  { value: 'REFUND', label: 'Refund' },
];

/**
 * TransactionFilters — header row of filters for the ledger table.
 *
 * Reports up:
 *   resourceTypes:    string[]
 *   transactionType:  string | null
 *   range:            { preset, since, until }
 *
 * The "Clear filters" chip only renders when at least one filter is
 * active; clicking it calls onClearFilters which the parent uses to
 * reset all three state buckets at once (prevents three back-to-back
 * refetches).
 */
export default function TransactionFilters({
  resourceTypes = [],
  transactionType = null,
  range = { preset: 'period' },
  onResourceTypesChange,
  onTransactionTypeChange,
  onRangeChange,
  onClearFilters,
}) {
  const [resourceOpen, setResourceOpen] = useState(false);
  const resourceRef = useRef(null);

  useEffect(() => {
    if (!resourceOpen) return undefined;
    const onClickOutside = (event) => {
      if (
        resourceRef.current &&
        !resourceRef.current.contains(event.target)
      ) {
        setResourceOpen(false);
      }
    };
    const onEscape = (event) => {
      if (event.key === 'Escape') setResourceOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEscape);
    };
  }, [resourceOpen]);

  const toggleResource = (value) => {
    if (resourceTypes.includes(value)) {
      onResourceTypesChange?.(resourceTypes.filter((v) => v !== value));
    } else {
      onResourceTypesChange?.([...resourceTypes, value]);
    }
  };

  const resourceLabel =
    resourceTypes.length === 0
      ? 'All resources'
      : resourceTypes.length === 1
      ? RESOURCE_TYPE_OPTIONS.find((o) => o.value === resourceTypes[0])
          ?.label ?? resourceTypes[0]
      : `${resourceTypes.length} resources`;

  const hasFilters =
    resourceTypes.length > 0 ||
    transactionType != null ||
    range.preset !== 'period';

  return (
    <div className="txn-filters">
      <div className="txn-filters__group" ref={resourceRef}>
        <button
          type="button"
          className={`txn-filters__pill txn-filters__pill--dropdown${
            resourceTypes.length > 0
              ? ' txn-filters__pill--active'
              : ''
          }`}
          onClick={() => setResourceOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={resourceOpen}
        >
          <Filter size={12} aria-hidden="true" />
          <span>{resourceLabel}</span>
          <ChevronDown size={12} aria-hidden="true" />
        </button>
        {resourceOpen && (
          <div className="txn-filters__dropdown" role="listbox">
            {RESOURCE_TYPE_OPTIONS.map((opt) => {
              const checked = resourceTypes.includes(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  className={`txn-filters__option${
                    checked ? ' txn-filters__option--checked' : ''
                  }`}
                  onClick={() => toggleResource(opt.value)}
                  role="option"
                  aria-selected={checked}
                >
                  <span className="txn-filters__checkbox">
                    {checked && <Check size={12} aria-hidden="true" />}
                  </span>
                  {opt.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="txn-filters__pills" role="radiogroup" aria-label="Transaction type">
        {TXN_TYPE_OPTIONS.map((opt) => {
          const selected = transactionType === opt.value;
          return (
            <button
              key={opt.label}
              type="button"
              className={`txn-filters__pill${
                selected ? ' txn-filters__pill--active' : ''
              }`}
              onClick={() => onTransactionTypeChange?.(opt.value)}
              role="radio"
              aria-checked={selected}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      <DateRangePicker value={range} onChange={onRangeChange} />

      {hasFilters && (
        <button
          type="button"
          className="txn-filters__clear"
          onClick={onClearFilters}
        >
          <X size={12} aria-hidden="true" />
          Clear filters
        </button>
      )}
    </div>
  );
}
